import { observer } from 'mobx-react-lite';
import { motion } from 'framer-motion';
import { usePaysReportController } from '@/controllers/report/usePaysReportController';
import { useReceivedPaysController } from '@/controllers/report/useReceivedPaysController';
import {
  useFinancesReportController,
  financeTabs,
} from '@/controllers/report/useFinancesReportController';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { chartColors } from '@/design/tokens';
import { StatusBars } from '@/components/charts/StatusBars';
import { ReportBarChart } from '@/components/reports/ReportBarChart';
import {
  DateRangeInputs,
  ReportExportMenu,
  ReportFilters,
  ReportLoadButton,
  ReportSelect,
  ReportTextInput,
} from '@/components/reports/ReportFilters';
import { ReportTable } from '@/components/reports/ReportTable';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { AsyncState } from '@/components/ui/AsyncState';
import { ReportPageHeader } from '@/components/layout/ReportPageHeader';
import { ChartTooltip } from '@/components/charts/ChartTooltip';
import { formatMoney } from '@/lib/format';
import type { CountryGroup } from '@/lib/currency';
import { formatDateTime } from '@/lib/dateUtils';
import { exportPaysToExcel, exportReceivedPaysToExcel } from '@/lib/exportExcel';
import {
  exportPaysToGoogleSheets,
  exportReceivedPaysToGoogleSheets,
} from '@/lib/exportGoogleSheets';
import { paymentStatusCls } from '@/lib/paymentStatus';

const C = chartColors;

type CountryTotal = Omit<CountryGroup<unknown>, 'items'> & { amount: number };
type CountryChart = Omit<CountryGroup<unknown>, 'items'> & {
  data: { label: string; amount: number }[];
};

// eslint-disable-next-line react-refresh/only-export-components
function SummaryCards({
  countLabel,
  count,
  totals,
}: {
  countLabel: string;
  count: number;
  totals: CountryTotal[];
}) {
  // Валюты стран не складываются: «Общая сумма» — отдельная карточка на каждую страну.
  const cards =
    totals.length > 0
      ? totals.map((total) => ({
          key: total.key,
          label: `Общая сумма · ${total.label}`,
          value: formatMoney(total.amount, total.currency),
        }))
      : [{ key: 'empty', label: 'Общая сумма', value: formatMoney(0) }];

  return (
    <div className={cn('grid gap-4 mb-6', cards.length > 1 ? 'grid-cols-3' : 'grid-cols-2')}>
      <div className='p-5 rounded-2xl border border-border/30 bg-card'>
        <p className='text-xs text-muted-foreground uppercase tracking-wider mb-2'>{countLabel}</p>
        <p className='text-3xl font-bold text-foreground'>{count}</p>
      </div>
      {cards.map((card) => (
        <div key={card.key} className='p-5 rounded-2xl border border-primary/20 bg-primary/5'>
          <p className='text-xs text-muted-foreground uppercase tracking-wider mb-2'>
            {card.label}
          </p>
          <p className='text-3xl font-bold text-primary'>{card.value}</p>
        </div>
      ))}
    </div>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
function AmountByDateCharts({
  charts,
  description,
  fill,
}: {
  charts: CountryChart[];
  description: string;
  fill: string;
}) {
  return charts.map((chart) => (
    <div
      key={chart.key}
      className={cn(
        'p-5 rounded-2xl border border-border/30 bg-card',
        charts.length === 1 && 'lg:col-span-2'
      )}
    >
      <ReportBarChart
        data={chart.data}
        title={`Суммы по датам · ${chart.label}`}
        description={chart.currency ? `${description}, ${chart.currency}` : description}
        fill={fill}
        tooltipContent={
          <ChartTooltip formatValue={(value) => formatMoney(value, chart.currency)} />
        }
        className='border-0 p-0 bg-transparent'
      />
    </div>
  ));
}

const PaysTab = observer(function PaysTab() {
  const paysReport = usePaysReportController();
  const byDate = paysReport.byDateCharts;
  const byStatus = paysReport.byStatusChartData();
  const tableHeaders = [
    { label: 'Дата', className: 'col-span-2' },
    { label: 'Тип', className: 'col-span-1' },
    { label: 'Сумма', className: 'col-span-2 text-center' },
    { label: 'Подтверждение', className: 'col-span-2' },
    { label: 'Статус', className: 'col-span-1' },
    { label: 'Телефон', className: 'col-span-1' },
    { label: 'Логин', className: 'col-span-2' },
    { label: 'Клиент', className: 'col-span-1' },
  ];

  return (
    <div>
      {/* Фильтры */}
      <ReportFilters>
        <DateRangeInputs
          start={paysReport.store.dateStart}
          end={paysReport.store.dateEnd}
          onStartChange={(value) => paysReport.set('dateStart', value)}
          onEndChange={(value) => paysReport.set('dateEnd', value)}
        />
        {paysReport.store.payTypes.length > 0 && (
          <ReportSelect
            value={paysReport.store.idPayType}
            onChange={(value) => paysReport.set('idPayType', value)}
            placeholder='Все типы'
            options={paysReport.store.payTypes.map((payType) => ({
              value: String(payType.id_pay_type),
              label: payType.type_name,
            }))}
          />
        )}
        {paysReport.store.payStatuses.length > 0 && (
          <ReportSelect
            value={paysReport.store.idStatuses}
            onChange={(value) => paysReport.set('idStatuses', value)}
            placeholder='Все статусы'
            options={paysReport.store.payStatuses.map((status) => ({
              value: String(status.id_pay_status),
              label: status.status_name,
            }))}
          />
        )}
        <ReportTextInput
          placeholder='Номер договора'
          value={paysReport.store.dogNumber}
          onChange={(value) => paysReport.set('dogNumber', value)}
          className='w-56'
        />
        <ReportLoadButton
          isLoading={paysReport.store.isLoading}
          onClick={() => void paysReport.load()}
        />
        <ReportExportMenu
          className='ml-auto'
          onExcelExport={() => exportPaysToExcel(paysReport.store.items, paysReport.countryOf)}
          onGoogleExport={() =>
            exportPaysToGoogleSheets(paysReport.store.items, paysReport.countryOf)
          }
        />
      </ReportFilters>

      {paysReport.store.error && <ErrorBanner message={paysReport.store.error} />}

      {/* Сводка */}
      <SummaryCards
        countLabel='Платежей'
        count={paysReport.store.items.length}
        totals={paysReport.totals}
      />

      {/* Графики */}
      {(byDate.length > 0 || byStatus.length > 0) && (
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6'>
          {byStatus.length > 0 && (
            <div className='p-5 rounded-2xl border border-border/30 bg-card'>
              <div className='text-sm font-semibold text-foreground mb-1'>Статусы</div>
              <div className='text-xs text-muted-foreground mb-5'>распределение по результату</div>
              <StatusBars data={byStatus} labelClassName='max-w-36' />
            </div>
          )}
          <AmountByDateCharts
            charts={byDate}
            description='платежи по датам оплаты'
            fill={C.secondary}
          />
        </div>
      )}

      {/* Таблица */}
      <AsyncState
        isLoading={paysReport.store.isLoading}
        error={null}
        isEmpty={paysReport.store.items.length === 0}
      >
        <ReportTable headers={tableHeaders} gapClassName='gap-3'>
          <div className='divide-y divide-border/10'>
            {paysReport.store.items.map((item, i) => (
              <div
                key={item.pay_code + i}
                className='grid grid-cols-12 gap-3 px-4 py-3 items-center text-center hover:bg-muted/20 transition-colors'
              >
                <div className='col-span-2 font-mono text-xs text-muted-foreground text-center'>
                  {formatDateTime(item.date_pay)}
                </div>
                <div className='col-span-1 text-xs text-foreground text-center truncate'>
                  {item.pay_type_name}
                </div>
                <div className='col-span-2 text-sm font-semibold text-center text-foreground'>
                  {formatMoney(item.money, paysReport.currencyOf(item))}
                </div>
                <div className='col-span-2 font-mono text-xs text-muted-foreground text-center'>
                  {formatDateTime(item.confirm_date)}
                </div>
                <div className='col-span-1 flex justify-center'>
                  <span
                    className={cn(
                      'text-xs px-2 py-0.5 rounded-lg border font-medium',
                      paymentStatusCls(item.status_name)
                    )}
                  >
                    {item.status_name}
                  </span>
                </div>
                <div className='col-span-1 font-mono text-xs text-muted-foreground text-center'>
                  {item.phone}
                </div>
                <div
                  className='col-span-2 font-mono text-xs text-foreground text-center'
                  title={item.login}
                >
                  {item.login}
                </div>
                <div className='col-span-1 text-xs text-muted-foreground text-center'>
                  {item.account_name}
                </div>
              </div>
            ))}
          </div>
        </ReportTable>
      </AsyncState>
    </div>
  );
});

const ReceivedTab = observer(function ReceivedTab() {
  const receivedPays = useReceivedPaysController();
  const byDate = receivedPays.byDateCharts;
  const byService = receivedPays.byServiceTypeChartData();
  const tableHeaders = [
    { label: 'Акк.', className: 'col-span-1' },
    { label: 'Дата периода', className: 'col-span-2' },
    { label: 'Дата изменения', className: 'col-span-2' },
    { label: 'Клиент', className: 'col-span-2' },
    { label: 'Логин', className: 'col-span-1' },
    { label: 'Сумма', className: 'col-span-2 text-center' },
    { label: 'Тип услуги', className: 'col-span-1' },
    { label: 'Камера', className: 'col-span-1' },
  ];

  return (
    <div>
      {/* Фильтры */}
      <ReportFilters>
        <DateRangeInputs
          start={receivedPays.store.dateStart}
          end={receivedPays.store.dateEnd}
          onStartChange={(value) => receivedPays.set('dateStart', value)}
          onEndChange={(value) => receivedPays.set('dateEnd', value)}
        />
        {receivedPays.store.countries.length > 0 && (
          <ReportSelect
            value={receivedPays.store.idCountries}
            onChange={(value) => receivedPays.set('idCountries', value)}
            placeholder='Все страны'
            options={receivedPays.store.countries.map((country) => ({
              value: country.id_country,
              label: country.name,
            }))}
          />
        )}
        <ReportLoadButton
          isLoading={receivedPays.store.isLoading}
          onClick={() => void receivedPays.load()}
        />
        <ReportExportMenu
          className='ml-auto'
          onExcelExport={() =>
            exportReceivedPaysToExcel(receivedPays.store.items, receivedPays.countryOf)
          }
          onGoogleExport={() =>
            exportReceivedPaysToGoogleSheets(receivedPays.store.items, receivedPays.countryOf)
          }
        />
      </ReportFilters>

      {receivedPays.store.error && <ErrorBanner message={receivedPays.store.error} />}

      {/* Сводка */}
      <SummaryCards
        countLabel='Записей'
        count={receivedPays.store.items.length}
        totals={receivedPays.totals}
      />

      {/* Графики */}
      {(byDate.length > 0 || byService.length > 0) && (
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6'>
          {byService.length > 0 && (
            <div className='p-5 rounded-2xl border border-border/30 bg-card'>
              <div className='text-sm font-semibold text-foreground mb-1'>Типы услуг</div>
              <div className='text-xs text-muted-foreground mb-5'>распределение по услугам</div>
              <StatusBars data={byService} labelClassName='max-w-36' />
            </div>
          )}
          <AmountByDateCharts
            charts={byDate}
            description='зачисления по датам изменения'
            fill={C.primary}
          />
        </div>
      )}

      {/* Таблица */}
      <AsyncState
        isLoading={receivedPays.store.isLoading}
        error={null}
        isEmpty={receivedPays.store.items.length === 0}
      >
        <ReportTable headers={tableHeaders} gapClassName='gap-3'>
          <div className='divide-y divide-border/10'>
            {receivedPays.store.items.map((item, i) => (
              <div
                key={`${item.id_account}-${item.id_camera ?? 'none'}-${i}`}
                className='grid grid-cols-12 gap-3 px-4 py-3 items-center text-center hover:bg-muted/20 transition-colors'
              >
                <div className='col-span-1 font-mono text-xs text-muted-foreground text-center'>
                  {item.id_account}
                </div>
                <div className='col-span-2 font-mono text-xs text-muted-foreground text-center'>
                  {formatDateTime(item.date_start_period)}
                </div>
                <div className='col-span-2 font-mono text-xs text-muted-foreground text-center'>
                  {formatDateTime(item.date_of_change)}
                </div>
                <div className='col-span-2 text-xs text-foreground text-center'>
                  {item.account_name}
                </div>
                <div
                  className='col-span-1 font-mono text-xs text-foreground text-center'
                  title={item.login}
                >
                  {item.login}
                </div>
                <div className='col-span-2 text-sm font-semibold text-center text-primary'>
                  {formatMoney(item.money, receivedPays.currencyOf(item))}
                </div>
                <div className='col-span-1 text-xs text-muted-foreground text-center'>
                  {item.service_type_name}
                </div>
                <div className='col-span-1 font-mono text-xs text-muted-foreground text-center'>
                  {item.id_camera ?? '—'}
                </div>
              </div>
            ))}
          </div>
        </ReportTable>
      </AsyncState>
    </div>
  );
});

export const FinancesReportPage = observer(function FinancesReportPage() {
  const { store, setActiveTab } = useFinancesReportController();

  return (
    <div className='min-h-screen bg-background'>
      <ReportPageHeader />

      <div className='px-6 pb-12'>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <div className='mb-6'>
            <h1 className='text-2xl font-medium tracking-wider text-foreground'>
              Отчёт по финансам
            </h1>
            <p className='text-sm text-muted-foreground mt-0.5'>
              Платежи и зачисления за выбранный период
            </p>
          </div>

          {/* Вкладки */}
          <div className='grid grid-cols-2 gap-1 p-1 rounded-2xl bg-muted/30 border border-border/30 mb-5'>
            {financeTabs.map((tab) => (
              <Button
                key={tab.key}
                variant='ghost'
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  'py-3 w-full text-sm font-semibold h-auto',
                  store.activeTab === tab.key
                    ? 'bg-card text-foreground shadow-sm border border-border/30'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {tab.label}
              </Button>
            ))}
          </div>

          {store.activeTab === 'pays' && <PaysTab />}
          {store.activeTab === 'received' && <ReceivedTab />}
        </motion.div>
      </div>
    </div>
  );
});
